import { noteDegradation } from "./degradations";

const STORAGE_KEY = "bt-objectArray-sawmillEventsV3";

/**
 * Set once a write has failed. From then on the memory queue below is the
 * source of truth and localStorage is not consulted again — a value written
 * before the failure is stale by definition, and reading it back would
 * resurrect events the memory queue already holds.
 */
let degraded = false;

/**
 * The queue while degraded. Page-scoped rather than persistent, so events are
 * lost on reload — but they were never going to reach localStorage anyway, and
 * holding them in memory keeps the batch threshold and flush path working for
 * the life of the page instead of dropping every event on the floor.
 */
let memoryQueue: unknown[] = [];

export function readQueue(): unknown[] {
  if (degraded) {
    // A copy, not the live array. The healthy path returns a freshly-parsed
    // array every call, so callers own what they get and `sawmillV3Plugin`
    // relies on that — it pushes the new event onto the result and lets
    // `enforceCaps` shift() off it before deciding whether to write. Handing
    // back the internal array would commit both mutations before the write,
    // so a failed flush would re-queue events already in the queue and a
    // trimmed event would be gone even on the path that never saves.
    return [...memoryQueue];
  }
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === null) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    // Guard the shape, not just the parse. Anything can write to this key, and
    // a non-array value (`{}`, `null`, `5`) would make the caller's
    // `queue.push` throw inside the navigator.locks callback — rejecting
    // `track`, which is fire-and-forget, and leaving the bad value in place so
    // every subsequent event fails the same way.
    return Array.isArray(parsed) ? (parsed as unknown[]) : [];
  } catch {
    return [];
  }
}

/**
 * Serialises the queue, dropping any single event `JSON.stringify` refuses.
 *
 * A circular structure or BigInt anywhere in one event throws for the whole
 * array, so serialising the batch as a unit means one malformed `extraInfo`
 * from any team costs every other team their events. Stringifying per event
 * contains the damage to the event that caused it.
 *
 * Returns `null` only if the *array* cannot be assembled, which should not
 * happen once each element is known-serialisable.
 */
function serialiseQueue(events: unknown[]): {
  json: string;
  dropped: number;
} {
  const serialised: string[] = [];
  let dropped = 0;
  for (const event of events) {
    try {
      const json = JSON.stringify(event);
      // stringify returns undefined for a bare undefined/function/symbol.
      if (json === undefined) {
        dropped++;
        continue;
      }
      serialised.push(json);
    } catch {
      dropped++;
    }
  }
  return { json: `[${serialised.join(",")}]`, dropped };
}

export function writeQueue(events: unknown[]): void {
  if (degraded) {
    // Copy on the way in as well, so a caller that keeps its array cannot
    // mutate the queue after the fact. The healthy path gets this for free
    // from JSON.stringify.
    memoryQueue = [...events];
    return;
  }
  // Serialise before touching storage, so an unserialisable event is dropped
  // on its own terms rather than being mistaken for a storage failure below.
  // Getting this wrong is not academic: the single catch that used to cover
  // both cases latched degraded mode on a circular value, silently ending
  // localStorage persistence for the rest of the page and reporting it as a
  // quota problem.
  const { json, dropped } = serialiseQueue(events);
  if (dropped > 0) {
    noteDegradation("unserializable-event");
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, json);
  } catch {
    // QuotaExceededError, or storage blocked outright. This runs inside the
    // navigator.locks callback in `track`, so an escaping error rejects a
    // fire-and-forget promise: no crash, no report, and every event from here
    // on silently gone.
    //
    // Quota is shared across everything the origin stores, so the analytics
    // queue can be the write that tips it over without being what filled it.
    degraded = true;
    memoryQueue = [...events];
    noteDegradation("local-storage");
  }
}

/**
 * Test seam: drops the degraded-mode state so one test's storage failure does
 * not leak into the next. Not exported from the package.
 */
export function resetStorageDegradation(): void {
  degraded = false;
  memoryQueue = [];
}
